import PauseIcon from '@material-ui/icons/Pause';
import PlayIcon from '@material-ui/icons/PlayArrow';
import {FormattedMessage} from 'react-intl';
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {Grid, Menu, Slider, MenuItem, IconButton, Typography} from '@material-ui/core';

import './VideoSlicer.scss';

type VideoSlicerProps = {
  currentTime: number;
  videoDuration: number;
  onChange: (changes: {
    marks?: Mark[];
    isPlaying?: boolean;
    currentTime?: number;
  }) => void;

  disabled?: boolean;
  disableMarks?: boolean;
};

type Mark = {
  value: number;
  label?: string;
};

type MenuPosition = {
  mouseX: number;
  mouseY: number;
} | null;

export const VideoSlicer = ({
  currentTime,
  videoDuration,
  disabled = false,
  disableMarks = false,
  onChange,
}: VideoSlicerProps) => {
  const animationRef = useRef<number>();
  const lastTimeRef = useRef<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [marks, setMarks] = useState<Mark[]>([]);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>(null);

  useEffect(() => {
    if (isPlaying) {
      const updateCurrentTime = (timestamp: number) => {
        if (currentTime >= videoDuration) {
          setIsPlaying(false);
          onChange({isPlaying: false, currentTime: videoDuration});
        } else {
          const deltaTime = timestamp - lastTimeRef.current;

          if (deltaTime >= 100) {
            onChange({currentTime: currentTime + 0.1});
            lastTimeRef.current = timestamp;
          }

          animationRef.current = requestAnimationFrame(updateCurrentTime);
        }
      };

      lastTimeRef.current = performance.now();
      animationRef.current = requestAnimationFrame(updateCurrentTime);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, currentTime, videoDuration, onChange]);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  }, []);

  const handlePlayPause = useCallback(() => {
    setIsPlaying(prevIsPlaying => {
      const newIsPlaying = !prevIsPlaying;

      onChange({isPlaying: newIsPlaying});

      return newIsPlaying;
    });
  }, [onChange]);

  const handleSliderChange = useCallback(
    (event: React.ChangeEvent<{}>, newValue: number | number[]) => {
      if (typeof newValue === 'number') {
        onChange({currentTime: newValue});
      }
    },
    [onChange]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent, mark: Mark | null = null) => {
      event.preventDefault();
      setMenuPosition({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
      });
    },
    []
  );

  const handleCloseMenu = useCallback(() => {
    setMenuPosition(null);
  }, []);

  const handleAddMark = useCallback(() => {
    setMarks(prevMarks => {
      const newMarks = [
        ...prevMarks,
        {
          label: formatTime(currentTime),
          value: currentTime,
        },
      ];

      onChange({marks: newMarks});

      return newMarks;
    });
    handleCloseMenu();
  }, [currentTime, formatTime, handleCloseMenu, onChange]);

  const handleRemoveMark = useCallback(
    markToRemove => {
      setMarks(prevMarks => {
        const newMarks = prevMarks.filter(mark => mark !== markToRemove);

        onChange({marks: newMarks});

        return newMarks;
      });
      handleCloseMenu();
    },
    [handleCloseMenu, onChange]
  );

  const calculateMarkPosition = useCallback(
    (value: number) => {
      return (value / videoDuration) * 100;
    },
    [videoDuration]
  );

  return (
    <div className={`video-slicer ${disabled ? 'disabled' : ''}`}>
      <Grid container spacing={1} alignItems="center">
        <Grid item className="video-config-dialog__play_button_container" xs={3}>
          <IconButton
            size="small"
            disabled={disabled}
            aria-label="play_pause"
            onClick={handlePlayPause}
          >
            {isPlaying ? (
              <PauseIcon fontSize="large" color="primary" />
            ) : (
              <PlayIcon fontSize="large" color="primary" />
            )}
          </IconButton>
        </Grid>
        <Grid item xs>
          <div
            className="video-slicer__slider-container"
            onContextMenu={event => handleContextMenu(event)}
            onClick={event => {
              if (!disabled) {
                const slider = event.currentTarget.getBoundingClientRect();
                const sliderWidth = slider.width;
                const clickPosition = event.clientX - slider.left;
                const newTime = (clickPosition / sliderWidth) * videoDuration;

                onChange({currentTime: newTime});
              }
            }}
          >
            <Slider
              min={0}
              step={0.1}
              disabled={disabled}
              value={currentTime}
              max={videoDuration}
              valueLabelDisplay="auto"
              onChange={handleSliderChange}
              aria-labelledby="video-slider"
              getAriaValueText={formatTime}
              valueLabelFormat={formatTime}
              className="video-slicer__slider"
            />
            {!disableMarks &&
              marks.map(mark => (
                <div
                  key={`mark-at-${mark.value}`}
                  className="video-slicer__mark"
                  style={{left: `${calculateMarkPosition(mark.value)}%`}}
                  onClick={event => {
                    event.preventDefault();
                    handleRemoveMark(mark);
                  }}
                >
                  <div className="video-slicer__mark-label">{mark.label}</div>
                </div>
              ))}
          </div>
        </Grid>
        <Grid item className="video-slicer__timer">
          <Typography>{`${formatTime(currentTime)} / ${formatTime(
            videoDuration
          )}`}</Typography>
        </Grid>
      </Grid>

      <Menu
        keepMounted
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        open={!disableMarks && menuPosition !== null}
        anchorPosition={
          menuPosition !== null
            ? {top: menuPosition.mouseY, left: menuPosition.mouseX}
            : undefined
        }
      >
        <MenuItem onClick={handleAddMark}>
          <FormattedMessage id="videoSlice.addMark" tagName="span" />
        </MenuItem>
      </Menu>
    </div>
  );
};
